import { Link } from 'react-router-dom';

import { Icon } from '@/components/ui';
import { cn } from '@/utils/cn';

/** Two letters is enough identity for a 48px tile. */
function initials(name) {
  if (!name) return '—';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

/**
 * The masthead of an admin record -- a company, a student, a listing.
 *
 * One component rather than three page-local headers, because the three
 * screens answer the same shape of question and an admin moves between them
 * all day: identity on the left, the decisions on the right, and the numbers
 * that qualify both along the bottom. Anything that moves between screens
 * costs a re-read.
 */
export function RecordHeader({
  backTo,
  backLabel,
  eyebrow,
  title,
  subtitle,
  monogram,
  leading,
  badges,
  actions,
  facts = [],
}) {
  return (
    <header className="border-line bg-surface rounded-card shadow-card mb-6 border">
      {backTo && (
        <div className="border-line border-b px-5 py-2">
          <Link
            to={backTo}
            className="text-ink-muted hover:text-ink inline-flex items-center gap-1 text-sm transition"
          >
            <Icon name="chevron" size={16} className="rotate-180" />
            {backLabel}
          </Link>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4 p-5">
        {/* An 18rem basis rather than 0: with a zero basis the identity block
            gave up all its width to the action buttons on a phone and the
            name stacked one letter per line. With a real basis the actions
            wrap below the name instead. */}
        <div className="flex min-w-0 flex-[1_1_18rem] items-start gap-4">
          {/* A `leading` element replaces the monogram where the record has a picture
              of its own -- a company's logo. */}
          {leading ?? (
            <span
              aria-hidden="true"
              className="bg-brand-50 text-brand-700 grid size-11 shrink-0 place-items-center rounded-xl text-base font-semibold"
            >
              {initials(monogram ?? title)}
            </span>
          )}

          <div className="min-w-0">
            {eyebrow && (
              <p className="text-ink-muted text-caption font-medium tracking-wide uppercase">
                {eyebrow}
              </p>
            )}
            <h1 className="text-ink mt-0.5 text-xl font-semibold tracking-tight break-words sm:text-2xl">
              {title}
            </h1>
            {subtitle && <p className="text-ink-muted mt-1 text-sm break-words">{subtitle}</p>}
            {badges && <div className="mt-2.5 flex flex-wrap items-center gap-2">{badges}</div>}
          </div>
        </div>

        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>

      {facts.length > 0 && (
        <dl
          className={cn(
            'border-line grid gap-px border-t',
            'bg-line sm:grid-cols-2',
            facts.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3',
          )}
        >
          {facts.map((fact) => (
            <div key={fact.label} className="bg-surface last:rounded-b-card px-5 py-3">
              <dt className="text-ink-muted text-caption font-medium tracking-wide uppercase">
                {fact.label}
              </dt>
              <dd className="text-ink mt-0.5 text-lg font-semibold tabular-nums">{fact.value}</dd>
              {fact.hint && <p className="text-ink-muted mt-0.5 text-xs">{fact.hint}</p>}
            </div>
          ))}
        </dl>
      )}
    </header>
  );
}
